import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Switch,
  FormGroup
} from '@mui/material';
import { Search, Refresh, AlertCircle, ExpandMore, Save, Clear } from '@mui/icons-material';

import { LoadRoles, getComponentPermissions, updateComponentPermissions, getDepartmentComponents, getAccessibleDepartments } from '../BaseURL';
import { getAllDepartments } from '../data/department';
import axios from 'axios';
import Swal from 'sweetalert2';

const ComponentAccess = () => {
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [departmentComponents, setDepartmentComponents] = useState({});
  const [componentPermissions, setComponentPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDepartments, setExpandedDepartments] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Load roles on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  // Load component permissions when role is selected
  useEffect(() => {
    if (selectedRole) {
      loadComponentPermissions();
    }
  }, [selectedRole]);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoles(response.data.results);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };



  const loadComponentPermissions = async () => {
    if (!selectedRole) return;

    console.log('Loading component permissions for role:', selectedRole);

    try {
      setLoading(true);
      
      // Step 1: Get accessible departments for this role from accessibledepartments table
      console.log('Step 1: Fetching accessible departments for role...');
      const accessibleDeptsResponse = await axios.get(getAccessibleDepartments(selectedRole));
      console.log('Accessible departments response:', accessibleDeptsResponse.data);
      
      if (!accessibleDeptsResponse.data.success) {
        throw new Error('Failed to load accessible departments');
      }
      
      const accessibleDepartmentIds = accessibleDeptsResponse.data.results.map(dept => dept.department_id);
      console.log('Accessible department IDs:', accessibleDepartmentIds);
      
      // Step 2: Get department details from local data and filter accessible ones
      console.log('Step 2: Getting department details from local data...');
      const allDepartments = getAllDepartments();
      console.log('All departments from local data:', allDepartments);
      
      // Filter departments to only show accessible ones
      const accessibleDepartments = allDepartments.filter(dept => 
        accessibleDepartmentIds.includes(dept.id)
      );
      console.log('Accessible departments:', accessibleDepartments);
      
      setDepartments(accessibleDepartments);
      
      // Step 3: Load components for each accessible department
      const componentsData = {};
      const permissionsData = {};

      for (const dept of accessibleDepartments) {
        console.log(`Step 3: Processing department: ${dept.name} (ID: ${dept.id})`);
        
        try {
          // Get all components for this department from department_components table
          console.log(`Fetching components for department ${dept.id}...`);
          const componentsResponse = await axios.get(getDepartmentComponents(dept.id));
          console.log('Components response:', componentsResponse.data);
          
          if (componentsResponse.data.success) {
            componentsData[dept.id] = componentsResponse.data.components;
            console.log(`Loaded ${componentsResponse.data.components.length} components for department ${dept.id}`);
          }

          // Get permissions for this role and department from component_permissions table
          console.log(`Fetching permissions for role ${selectedRole} and department ${dept.id}...`);
          const permissionsResponse = await axios.get(getComponentPermissions(selectedRole, dept.id));
          console.log('Permissions response:', permissionsResponse.data);
          
          if (permissionsResponse.data.success) {
            permissionsData[dept.id] = permissionsResponse.data.permissions;
            console.log(`Loaded permissions for department ${dept.id}:`, permissionsResponse.data.permissions);
            console.log(`Permissions data structure for department ${dept.id}:`, typeof permissionsResponse.data.permissions, permissionsResponse.data.permissions);
          }
        } catch (error) {
          console.error(`Error loading data for department ${dept.id}:`, error);
        }
      }

      console.log('Final components data:', componentsData);
      console.log('Final permissions data:', permissionsData);

      setDepartmentComponents(componentsData);
      setComponentPermissions(permissionsData);
    } catch (error) {
      console.error('Error loading component permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setExpandedDepartments(new Set());
    setHasChanges(false);
  };

  const handleDepartmentToggle = (departmentId) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentId)) {
        newSet.delete(departmentId);
      } else {
        newSet.add(departmentId);
      }
      return newSet;
    });
  };

  const handlePermissionChange = (departmentId, componentName, checked) => {
    console.log(`Setting access for ${componentName} in department ${departmentId} to: ${checked}`);
    
    setComponentPermissions(prev => {
      const newPermissions = { ...prev };
      
      if (!newPermissions[departmentId]) {
        newPermissions[departmentId] = {};
      }
      
      newPermissions[departmentId][componentName] = {
        access: checked
      };
      
      return newPermissions;
    });
    
    setHasChanges(true);
  };

  const savePermissions = async () => {
    if (!selectedRole || !hasChanges) return;

    console.log('Saving permissions for role:', selectedRole);
    console.log('Permissions to save:', componentPermissions);

    try {
      setLoading(true);
      
      const permissionsToUpdate = [];
      
      Object.entries(componentPermissions).forEach(([departmentId, components]) => {
        Object.entries(components).forEach(([componentName, permissions]) => {
          console.log(`Preparing permission: roleId=${selectedRole}, departmentId=${departmentId}, componentName=${componentName}, permissions=`, permissions);
          permissionsToUpdate.push({
            roleId: parseInt(selectedRole),
            departmentId: parseInt(departmentId),
            componentName,
            permissions: JSON.stringify(permissions)
          });
        });
      });

      console.log('Final permissions array to send:', permissionsToUpdate);

      const response = await axios.post(updateComponentPermissions, {
        permissions: permissionsToUpdate
      });

      console.log('Save response:', response.data);

      if (response.data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Component access updated successfully',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to update component access',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetChanges = () => {
    loadComponentPermissions();
    setHasChanges(false);
  };

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPermissionStatus = (departmentId, componentName) => {
    const permissions = componentPermissions[departmentId]?.[componentName];
    console.log(`Getting permission status for ${componentName} in department ${departmentId}:`, permissions);
    if (!permissions) return { access: false };
    return permissions;
  };

  const hasAccess = (departmentId, componentName) => {
    const permissions = getPermissionStatus(departmentId, componentName);
    const hasAccess = permissions.access === true;
    console.log(`Access check for ${componentName} in department ${departmentId}: ${hasAccess}`);
    return hasAccess;
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
            Component Access Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {hasChanges && (
              <>
                <Button
                  variant="outlined"
                  onClick={resetChanges}
                  startIcon={<Clear />}
                  disabled={loading}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={savePermissions}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  Save Changes
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
          Manage component access permissions for different roles across departments
        </Typography>

        {/* Role Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Select Role
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={handleRoleChange}
                label="Role"
                disabled={rolesLoading}
              >
                {rolesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading roles...
                  </MenuItem>
                ) : (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.role}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {selectedRole && (
          <>
            {/* Search and Filters */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <TextField
                  fullWidth
                  placeholder="Search departments..."
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </CardContent>
            </Card>

            {/* Departments and Components */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={48} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredDepartments.map((department) => {
                  const components = departmentComponents[department.id] || [];
                  const isExpanded = expandedDepartments.has(department.id);
                  
                  return (
                    <Accordion
                      key={department.id}
                      expanded={isExpanded}
                      onChange={() => handleDepartmentToggle(department.id)}
                      sx={{
                        '&:before': { display: 'none' },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: isExpanded ? 'primary.light' : 'grey.50',
                          '&:hover': { bgcolor: 'primary.light' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {department.name}
                          </Typography>
                          <Chip 
                            label={`${components.length} components`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      </AccordionSummary>
                      
                      <AccordionDetails sx={{ p: 0 }}>
                        <Box sx={{ p: 2 }}>
                          {components.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              No components assigned to this department
                            </Typography>
                          ) : (
                            <Grid container spacing={2}>
                              {components.map((component) => {
                                const hasComponentAccess = hasAccess(department.id, component.component_name);
                                
                                return (
                                  <Grid item xs={12} key={component.component_name}>
                                    <Card variant="outlined" sx={{ p: 2 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={hasComponentAccess}
                                              onChange={(e) => handlePermissionChange(
                                                department.id,
                                                component.component_name,
                                                e.target.checked
                                              )}
                                            />
                                          }
                                          label={
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                              {component.component_name}
                                            </Typography>
                                          }
                                        />
                                        {hasComponentAccess && (
                                          <Chip 
                                            label="Access Granted" 
                                            color="success" 
                                            size="small" 
                                            variant="outlined"
                                          />
                                        )}
                                      </Box>
                                      
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {component.description}
                                      </Typography>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {!selectedRole && !rolesLoading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please select a role to manage component permissions
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ComponentAccess;